// B1237: the ONE reader and writer of this origin's persisted session.
//
// WHY THIS FILE EXISTS, AND WHY IT IMPORTS NOTHING
//
// The console logs in once and keeps `{token, expires_at_ms, username}` under
// `localStorage['kinedb.session']`. The four demo pages under `web/demos/` are
// the SAME ORIGIN, so they can read that entry and stop asking the reader for a
// username and a password on every page and every reload — the owner's report of
// 2026-09-22.
//
// A demo page must NOT import `auth-store.js` to get it. That module's body
// imports `@kinedb/client` and calls `setAuthHooks`, so importing it would pull
// the console's machinery into a public demonstration bundle
// (`web/demos/README.md` forbids exactly that). So the storage key, the expiry
// rule and the malformed-entry rule live HERE, with NO import at all, and both
// sides read this one copy. A second copy is how the two drift, and how a demo
// starts trusting a token the console has already dropped.
//
// The security stance for the token being in `localStorage` at all — what a
// bearer is for, the 24 h TTL, the revocation paths, the residual XSS risk — is
// recorded in `archive/2026-08-17-ui-persistent-session-token-ws-reauth.md` and
// in the header of `auth-store.js`. It is not restated here.
//
// EVERY read and EVERY write is wrapped: a private-mode or blocked origin throws
// on the accessor itself, and that must degrade to "no session", never to an
// exception on a page that would otherwise work offline.

/** The one key the console writes and the demos read. */
export const SESSION_STORAGE_KEY = 'kinedb.session';

/**
 * The default store. Resolved per call, not at module load: bun has no
 * `localStorage` global, and a test installs one after this module is imported.
 */
function defaultStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Remove the entry, swallowing a storage that refuses. */
function drop(storage) {
  try {
    storage?.removeItem(SESSION_STORAGE_KEY);
  } catch { /* private mode / no storage */ }
}

/**
 * The persisted session, if it is live AND well-formed. Otherwise `null`.
 *
 * Well-formed is exactly what the console has always written: a non-empty string
 * `token`, a string `username`, and a finite `expires_at_ms`. Live is
 * `Date.now() < expires_at_ms` — the clock check is the only expiry signal that
 * costs no round trip, and it stops a tab that has been closed for a day from
 * presenting a token it can already prove is dead.
 *
 * Anything else is dropped AND REMOVED, so a bad entry cannot survive to be
 * re-read on the next load. An ABSENT key is not a bad entry: it is left alone,
 * so an anonymous visitor's every page load writes nothing.
 *
 * @param {Storage|null} [storage] defaults to `globalThis.localStorage`.
 * @returns {{token: string, expires_at_ms: number, username: string}|null}
 */
export function readLiveSession(storage = defaultStorage()) {
  let raw = null;
  try {
    raw = storage?.getItem(SESSION_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const j = JSON.parse(raw);
    const expires_at_ms = Number(j?.expires_at_ms);
    if (typeof j?.token === 'string' && j.token && typeof j?.username === 'string'
        && Number.isFinite(expires_at_ms) && Date.now() < expires_at_ms) {
      return { token: j.token, expires_at_ms, username: j.username };
    }
  } catch { /* malformed */ }
  drop(storage);
  return null;
}

/**
 * Persist a session, or remove it when `session` is `null`.
 *
 * ONLY the three fields are written. A caller may pass a whole login result,
 * password and all, and the extra keys never reach the disk — that is what keeps
 * the password out of storage, and it is today's behaviour, kept.
 *
 * @param {{token: string, expires_at_ms: number, username: string}|null} session
 * @param {Storage|null} [storage] defaults to `globalThis.localStorage`.
 */
export function writeSession(session, storage = defaultStorage()) {
  if (!session) {
    drop(storage);
    return;
  }
  try {
    const { token, expires_at_ms, username } = session;
    storage?.setItem(SESSION_STORAGE_KEY, JSON.stringify({ token, expires_at_ms, username }));
  } catch { /* private mode / no storage: in-memory only, as before */ }
}
